/**
 * @deprecated Please migrate to semantic color tokens from `@creditornot/cb-ingredients/design-tokens` instead.
 * Consult your designer for the appropriate color token to use or ask `#ask-design-systems` channel in Slack when in doubt.
 *
 * Learn more about semantic colors in the design system documentation: https://designsystem.wolt.com/foundations/color
 *
 * Note that currently, not all colors might have a direct replacement in the semantic color tokens.
 * These cases should be handled on a case-by-case basis.
 *
 * Migrating away from these colors is crucial for the following reasons:
 * * It ensures that the colors are consistent with the rest of the design system.
 * * It ensures that the colors are accessible and will comply with the EAA (European Accessibility Act) requirements by June 2025.
 * * It makes your application properly themeable.
 */
export const colors = {
  /**
   * Potential replacements (based on light mode):
   * * `#009DE0` (hard coded Wolt blue for e.g. logos and W+ badges)
   * * `color.bgFillBrand`
   * * `color.textBrand`
   * * `color.borderSelected`
   */
  wolt100: 'rgba(0, 157, 224, 1)',
  wolt96: 'rgba(0, 157, 224, 0.96)',
  /**
   *  Potential replacements (based on light mode):
   * * `color.bgFillBrandHovered`
   */
  wolt92: 'rgba(0, 157, 224, 0.92)',
  /**
   * Potential replacements (based on light mode):
   * * `color.bgFillBrandSelected`
   */
  wolt88: 'rgba(0, 157, 224, 0.88)',
  wolt84: 'rgba(0, 157, 224, 0.84)',
  /**
   * Potential replacements (based on light mode):
   * * `color.bgFillBrandPressed`
   */
  wolt80: 'rgba(0, 157, 224, 0.80)',
  wolt76: 'rgba(0, 157, 224, 0.76)',
  wolt72: 'rgba(0, 157, 224, 0.72)',
  wolt68: 'rgba(0, 157, 224, 0.68)',
  wolt64: 'rgba(0, 157, 224, 0.64)',
  wolt60: 'rgba(0, 157, 224, 0.60)',
  wolt56: 'rgba(0, 157, 224, 0.56)',
  wolt52: 'rgba(0, 157, 224, 0.52)',
  wolt48: 'rgba(0, 157, 224, 0.48)',
  wolt44: 'rgba(0, 157, 224, 0.44)',
  /**
   * Potential replacements (based on light mode):
   * * `color.bgFillBrandDisabled`
   */
  wolt40: 'rgba(0, 157, 224, 0.40)',
  wolt36: 'rgba(0, 157, 224, 0.36)',
  wolt32: 'rgba(0, 157, 224, 0.32)',
  wolt28: 'rgba(0, 157, 224, 0.28)',
  wolt24: 'rgba(0, 157, 224, 0.24)',
  wolt20: 'rgba(0, 157, 224, 0.20)',
  wolt16: 'rgba(0, 157, 224, 0.16)',
  /**
   * Potential replacements (based on light mode):
   * * `color.bgSurfaceBrand`
   */
  wolt12: 'rgba(0, 157, 224, 0.12)',
  wolt8: 'rgba(0, 157, 224, 0.08)',
  wolt4: 'rgba(0, 157, 224, 0.04)',

  /**
   * Potential replacements (based on light mode):
   * * `color.text`
   * * `color.textOnBgFillInverse`
   * * `color.textOnBgFillInverseHovered`
   * * `color.textOnBgFillInversePressed`
   * * `color.textOnBgFillInverseSelected`
   * * `color.textTransparent`
   * * `color.textOnBgFillTransparent`
   * * `color.textOnBgFillTransparentHovered`
   * * `color.textOnBgFillTransparentPressed`
   * * `color.textOnBgFillTransparentSelected`
   */
  pepper100: 'rgba(32, 33, 37, 1)',
  pepper96: 'rgba(32, 33, 37, 0.96)',
  pepper92: 'rgba(32, 33, 37, 0.92)',
  pepper88: 'rgba(32, 33, 37, 0.88)',
  pepper84: 'rgba(32, 33, 37, 0.84)',
  pepper80: 'rgba(32, 33, 37, 0.80)',
  pepper76: 'rgba(32, 33, 37, 0.76)',
  pepper72: 'rgba(32, 33, 37, 0.72)',
  pepper68: 'rgba(32, 33, 37, 0.68)',
  pepper64: 'rgba(32, 33, 37, 0.64)',
  pepper60: 'rgba(32, 33, 37, 0.60)',
  pepper56: 'rgba(32, 33, 37, 0.56)',
  pepper52: 'rgba(32, 33, 37, 0.52)',
  pepper48: 'rgba(32, 33, 37, 0.48)',
  pepper44: 'rgba(32, 33, 37, 0.44)',
  pepper40: 'rgba(32, 33, 37, 0.40)',
  pepper36: 'rgba(32, 33, 37, 0.36)',
  pepper32: 'rgba(32, 33, 37, 0.32)',
  pepper28: 'rgba(32, 33, 37, 0.28)',
  pepper24: 'rgba(32, 33, 37, 0.24)',
  pepper20: 'rgba(32, 33, 37, 0.20)',
  pepper16: 'rgba(32, 33, 37, 0.16)',
  /**
   * Potential replacements (based on light mode):
   * * `color.border`
   */
  pepper12: 'rgba(32, 33, 37, 0.12)',
  /**
   * Potential replacements (based on light mode):
   * * `color.border`
   * * `color.bgSurface`
   */
  pepper8: 'rgba(32, 33, 37, 0.08)',
  pepper4: 'rgba(32, 33, 37, 0.04)',
  pepper2: 'rgba(32, 33, 37, 0.02)',

  /**
   * Potential replacements (based on light mode):
   * * `color.bg`
   * * `color.textOnBgFill`
   * * `color.textOnBgFillHovered`
   * * `color.textOnBgFillPressed`
   * * `color.textOnBgFillSelected`
   * * `color.textOnBgFillBrand`
   * * `color.textOnBgFillBrandHovered`
   * * `color.textOnBgFillBrandPressed`
   * * `color.textOnBgFillBrandSelected`
   * * `color.textOnBgFillNegative`
   * * `color.textOnBgFillNegativeHovered`
   * * `color.textOnBgFillNegativePressed`
   * * `color.textOnBgFillNegativeSelected`
   * * `color.textOnBgFillWarning`
   * * `color.textOnBgFillWarningHovered`
   * * `color.textOnBgFillWarningPressed`
   * * `color.textOnBgFillWarningSelected`
   * * `color.textOnBgFillPositive`
   * * `color.textOnBgFillPositiveHovered`
   * * `color.textOnBgFillPositivePressed`
   * * `color.textOnBgFillPositiveSelected`
   * * `color.bgFillInverse`
   * * `color.textInverse`
   * * `color.fixedWhite` (white on all theme modes)
   */
  salt100: 'rgba(255, 255, 255, 1)',
  salt96: 'rgba(255, 255, 255, 0.96)',
  salt92: 'rgba(255, 255, 255, 0.92)',
  salt88: 'rgba(255, 255, 255, 0.88)',
  salt84: 'rgba(255, 255, 255, 0.84)',
  salt80: 'rgba(255, 255, 255, 0.80)',
  salt76: 'rgba(255, 255, 255, 0.76)',
  salt72: 'rgba(255, 255, 255, 0.72)',
  salt68: 'rgba(255, 255, 255, 0.68)',
  salt64: 'rgba(255, 255, 255, 0.64)',
  salt60: 'rgba(255, 255, 255, 0.60)',
  salt56: 'rgba(255, 255, 255, 0.56)',
  salt52: 'rgba(255, 255, 255, 0.52)',
  salt48: 'rgba(255, 255, 255, 0.48)',
  salt44: 'rgba(255, 255, 255, 0.44)',
  salt40: 'rgba(255, 255, 255, 0.40)',
  salt36: 'rgba(255, 255, 255, 0.36)',
  salt32: 'rgba(255, 255, 255, 0.32)',
  salt28: 'rgba(255, 255, 255, 0.28)',
  salt24: 'rgba(255, 255, 255, 0.24)',
  salt20: 'rgba(255, 255, 255, 0.20)',
  salt16: 'rgba(255, 255, 255, 0.16)',
  salt12: 'rgba(255, 255, 255, 0.12)',
  salt8: 'rgba(255, 255, 255, 0.08)',
  salt4: 'rgba(255, 255, 255, 0.04)',

  darkSky100: 'rgba(0, 120, 212, 1)',
  darkSky96: 'rgba(0, 120, 212, 0.96)',
  darkSky92: 'rgba(0, 120, 212, 0.92)',
  darkSky88: 'rgba(0, 120, 212, 0.88)',
  darkSky84: 'rgba(0, 120, 212, 0.84)',
  darkSky80: 'rgba(0, 120, 212, 0.80)',
  darkSky76: 'rgba(0, 120, 212, 0.76)',
  darkSky72: 'rgba(0, 120, 212, 0.72)',
  darkSky68: 'rgba(0, 120, 212, 0.68)',
  darkSky64: 'rgba(0, 120, 212, 0.64)',
  darkSky60: 'rgba(0, 120, 212, 0.60)',
  darkSky56: 'rgba(0, 120, 212, 0.56)',
  darkSky52: 'rgba(0, 120, 212, 0.52)',
  darkSky48: 'rgba(0, 120, 212, 0.48)',
  darkSky44: 'rgba(0, 120, 212, 0.44)',
  darkSky40: 'rgba(0, 120, 212, 0.40)',
  darkSky36: 'rgba(0, 120, 212, 0.36)',
  darkSky32: 'rgba(0, 120, 212, 0.32)',
  darkSky28: 'rgba(0, 120, 212, 0.28)',
  darkSky24: 'rgba(0, 120, 212, 0.24)',
  darkSky20: 'rgba(0, 120, 212, 0.20)',
  darkSky16: 'rgba(0, 120, 212, 0.16)',
  darkSky12: 'rgba(0, 120, 212, 0.12)',
  darkSky8: 'rgba(0, 120, 212, 0.08)',
  darkSky4: 'rgba(0, 120, 212, 0.04)',

  nightSky100: 'rgba(15, 37, 148, 1)',
  nightSky96: 'rgba(15, 37, 148, 0.96)',
  nightSky92: 'rgba(15, 37, 148, 0.92)',
  nightSky88: 'rgba(15, 37, 148, 0.88)',
  nightSky84: 'rgba(15, 37, 148, 0.84)',
  nightSky80: 'rgba(15, 37, 148, 0.80)',
  nightSky76: 'rgba(15, 37, 148, 0.76)',
  nightSky72: 'rgba(15, 37, 148, 0.72)',
  nightSky68: 'rgba(15, 37, 148, 0.68)',
  nightSky64: 'rgba(15, 37, 148, 0.64)',
  nightSky60: 'rgba(15, 37, 148, 0.60)',
  nightSky56: 'rgba(15, 37, 148, 0.56)',
  nightSky52: 'rgba(15, 37, 148, 0.52)',
  nightSky48: 'rgba(15, 37, 148, 0.48)',
  nightSky44: 'rgba(15, 37, 148, 0.44)',
  nightSky40: 'rgba(15, 37, 148, 0.40)',
  nightSky36: 'rgba(15, 37, 148, 0.36)',
  nightSky32: 'rgba(15, 37, 148, 0.32)',
  nightSky28: 'rgba(15, 37, 148, 0.28)',
  nightSky24: 'rgba(15, 37, 148, 0.24)',
  nightSky20: 'rgba(15, 37, 148, 0.20)',
  nightSky16: 'rgba(15, 37, 148, 0.16)',
  nightSky12: 'rgba(15, 37, 148, 0.12)',
  nightSky8: 'rgba(15, 37, 148, 0.08)',
  nightSky4: 'rgba(15, 37, 148, 0.04)',

  /**
   * Potential replacements (based on light mode):
   * * `color.fixedBlack` (black on all thememodes)
   */
  space100: 'rgba(0,0,0, 1)',
  space96: 'rgba(0,0,0, 0.96)',
  space92: 'rgba(0,0,0, 0.92)',
  space88: 'rgba(0,0,0, 0.88)',
  space84: 'rgba(0,0,0, 0.84)',
  space80: 'rgba(0,0,0, 0.80)',
  space76: 'rgba(0,0,0, 0.76)',
  space72: 'rgba(0,0,0, 0.72)',
  space68: 'rgba(0,0,0, 0.68)',
  space64: 'rgba(0,0,0, 0.64)',
  space60: 'rgba(0,0,0, 0.60)',
  space56: 'rgba(0,0,0, 0.56)',
  space52: 'rgba(0,0,0, 0.52)',
  space48: 'rgba(0,0,0, 0.48)',
  space44: 'rgba(0,0,0, 0.44)',
  space40: 'rgba(0,0,0, 0.40)',
  space36: 'rgba(0,0,0, 0.36)',
  space32: 'rgba(0,0,0, 0.32)',
  space28: 'rgba(0,0,0, 0.28)',
  space24: 'rgba(0,0,0, 0.24)',
  space20: 'rgba(0,0,0, 0.20)',
  space16: 'rgba(0,0,0, 0.16)',
  space12: 'rgba(0,0,0, 0.12)',
  /**
   * Potential replacements (based on light mode):
   * * `color.bgSurface`
   */
  space8: 'rgba(0,0,0, 0.08)',
  space4: 'rgba(0,0,0, 0.04)',

  /**
   * Potential replacements (based on light mode):
   * * `color.bgFillPositive`
   * * `color.textPositive`
   */
  lime100: 'rgba(31, 199, 10, 1)',
  lime96: 'rgba(31, 199, 10, 0.96)',
  /**
   * Potential replacements (based on light mode):
   * * `color.textPositiveHovered`
   */
  lime92: 'rgba(31, 199, 10, 0.92)',
  /**
   * Potential replacements (based on light mode):
   * * `color.textPositiveSelected`
   */
  lime88: 'rgba(31, 199, 10, 0.88)',
  lime84: 'rgba(31, 199, 10, 0.84)',
  /**
   * Potential replacements (based on light mode):
   * * `color.textPositivePressed`
   */
  lime80: 'rgba(31, 199, 10, 0.80)',
  lime76: 'rgba(31, 199, 10, 0.76)',
  lime72: 'rgba(31, 199, 10, 0.72)',
  lime68: 'rgba(31, 199, 10, 0.68)',
  lime64: 'rgba(31, 199, 10, 0.64)',
  lime60: 'rgba(31, 199, 10, 0.60)',
  lime56: 'rgba(31, 199, 10, 0.56)',
  lime52: 'rgba(31, 199, 10, 0.52)',
  lime48: 'rgba(31, 199, 10, 0.48)',
  lime44: 'rgba(31, 199, 10, 0.44)',
  /**
   * Potential replacements (based on light mode):
   * * `color.textPositiveDisabled`
   */
  lime40: 'rgba(31, 199, 10, 0.40)',
  lime36: 'rgba(31, 199, 10, 0.36)',
  lime32: 'rgba(31, 199, 10, 0.32)',
  lime28: 'rgba(31, 199, 10, 0.28)',
  lime24: 'rgba(31, 199, 10, 0.24)',
  lime20: 'rgba(31, 199, 10, 0.20)',
  lime16: 'rgba(31, 199, 10, 0.16)',
  lime12: 'rgba(31, 199, 10, 0.12)',
  /**
   * Potential replacements (based on light mode):
   * * `color.bgSurfacePositive`
   */
  lime8: 'rgba(31, 199, 10, 0.08)',
  lime4: 'rgba(31, 199, 10, 0.04)',

  /**
   * Potential replacements (based on light mode):
   * * `color.bgFillWarning`
   * * `color.textWarning`
   */
  banana100: 'rgba(254, 169, 13, 1)',
  banana96: 'rgba(254, 169, 13, 0.96)',
  /**
   * Potential replacements (based on light mode):
   * * `color.textWarningHovered`
   */
  banana92: 'rgba(254, 169, 13, 0.92)',
  /**
   * Potential replacements (based on light mode):
   * * `color.textWarningSelected`
   */
  banana88: 'rgba(254, 169, 13, 0.88)',
  banana84: 'rgba(254, 169, 13, 0.84)',
  /**
   * Potential replacements (based on light mode):
   * * `color.textWarningPressed`
   */
  banana80: 'rgba(254, 169, 13, 0.80)',
  banana76: 'rgba(254, 169, 13, 0.76)',
  banana72: 'rgba(254, 169, 13, 0.72)',
  banana68: 'rgba(254, 169, 13, 0.68)',
  banana64: 'rgba(254, 169, 13, 0.64)',
  banana60: 'rgba(254, 169, 13, 0.60)',
  banana56: 'rgba(254, 169, 13, 0.56)',
  banana52: 'rgba(254, 169, 13, 0.52)',
  banana48: 'rgba(254, 169, 13, 0.48)',
  banana44: 'rgba(254, 169, 13, 0.44)',
  /**
   * Potential replacements (based on light mode):
   * * `color.textWarningDisabled`
   */
  banana40: 'rgba(254, 169, 13, 0.40)',
  banana36: 'rgba(254, 169, 13, 0.36)',
  banana32: 'rgba(254, 169, 13, 0.32)',
  banana28: 'rgba(254, 169, 13, 0.28)',
  banana24: 'rgba(254, 169, 13, 0.24)',
  banana20: 'rgba(254, 169, 13, 0.20)',
  banana16: 'rgba(254, 169, 13, 0.16)',
  banana12: 'rgba(254, 169, 13, 0.12)',
  /**
   * Potential replacements (based on light mode):
   * * `color.bgSurfaceWarning`
   */
  banana8: 'rgba(254, 169, 13, 0.08)',
  banana4: 'rgba(254, 169, 13, 0.04)',

  /**
   * Potential replacements (based on light mode):
   * * `color.bgFillWarning`
   * * `color.textWarning`
   */
  orange100: 'rgba(252, 98, 0, 1)',
  orange96: 'rgba(252, 98, 0, 0.96)',
  /**
   * Potential replacements (based on light mode):
   * * `color.textWarningHovered`
   */
  orange92: 'rgba(252, 98, 0, 0.92)',
  /**
   * Potential replacements (based on light mode):
   * * `color.textWarningSelected`
   */
  orange88: 'rgba(252, 98, 0, 0.88)',
  orange84: 'rgba(252, 98, 0, 0.84)',
  /**
   * Potential replacements (based on light mode):
   * * `color.textWarningPressed`
   */
  orange80: 'rgba(252, 98, 0, 0.80)',
  orange76: 'rgba(252, 98, 0, 0.76)',
  orange72: 'rgba(252, 98, 0, 0.72)',
  orange68: 'rgba(252, 98, 0, 0.68)',
  orange64: 'rgba(252, 98, 0, 0.64)',
  orange60: 'rgba(252, 98, 0, 0.60)',
  orange56: 'rgba(252, 98, 0, 0.56)',
  orange52: 'rgba(252, 98, 0, 0.52)',
  orange48: 'rgba(252, 98, 0, 0.48)',
  orange44: 'rgba(252, 98, 0, 0.44)',
  /**
   * Potential replacements (based on light mode):
   * * `color.textWarningDisabled`
   */
  orange40: 'rgba(252, 98, 0, 0.40)',
  orange36: 'rgba(252, 98, 0, 0.36)',
  orange32: 'rgba(252, 98, 0, 0.32)',
  orange28: 'rgba(252, 98, 0, 0.28)',
  orange24: 'rgba(252, 98, 0, 0.24)',
  orange20: 'rgba(252, 98, 0, 0.20)',
  orange16: 'rgba(252, 98, 0, 0.16)',
  orange12: 'rgba(252, 98, 0, 0.12)',
  /**
   * Potential replacements (based on light mode):
   * * `color.bgSurfaceWarning`
   */
  orange8: 'rgba(252, 98, 0, 0.08)',
  orange4: 'rgba(252, 98, 0, 0.04)',

  /**
   * Potential replacements (based on light mode):
   * * `color.borderNegative`
   * * `color.bgFillNegative`
   * * `color.textNegative`
   */
  strawberry100: 'rgba(249, 58, 37, 1)',
  strawberry96: 'rgba(249, 58, 37, 0.96)',
  /**
   * Potential replacements (based on light mode):
   * * `color.textNegativeHovered`
   */
  strawberry92: 'rgba(249, 58, 37, 0.92)',
  /**
   * Potential replacements (based on light mode):
   * * `color.textNegativeSelected`
   */
  strawberry88: 'rgba(249, 58, 37, 0.88)',
  strawberry84: 'rgba(249, 58, 37, 0.84)',
  strawberry80: 'rgba(249, 58, 37, 0.80)',
  strawberry76: 'rgba(249, 58, 37, 0.76)',
  strawberry72: 'rgba(249, 58, 37, 0.72)',
  strawberry68: 'rgba(249, 58, 37, 0.68)',
  strawberry64: 'rgba(249, 58, 37, 0.64)',
  strawberry60: 'rgba(249, 58, 37, 0.60)',
  strawberry56: 'rgba(249, 58, 37, 0.56)',
  strawberry52: 'rgba(249, 58, 37, 0.52)',
  strawberry48: 'rgba(249, 58, 37, 0.48)',
  strawberry44: 'rgba(249, 58, 37, 0.44)',
  /**
   * Potential replacements (based on light mode):
   * * `color.textNegativeDisabled`
   */
  strawberry40: 'rgba(249, 58, 37, 0.40)',
  strawberry36: 'rgba(249, 58, 37, 0.36)',
  strawberry32: 'rgba(249, 58, 37, 0.32)',
  strawberry28: 'rgba(249, 58, 37, 0.28)',
  strawberry24: 'rgba(249, 58, 37, 0.24)',
  strawberry20: 'rgba(249, 58, 37, 0.20)',
  strawberry16: 'rgba(249, 58, 37, 0.16)',
  strawberry12: 'rgba(249, 58, 37, 0.12)',
  /**
   * Potential replacements (based on light mode):
   * * `color.bgSurfaceNegative`
   */
  strawberry8: 'rgba(249, 58, 37, 0.08)',
  strawberry4: 'rgba(249, 58, 37, 0.04)',

  plum100: 'rgba(135, 29, 196, 1)',
  plum96: 'rgba(135, 29, 196, 0.96)',
  plum92: 'rgba(135, 29, 196, 0.92)',
  plum88: 'rgba(135, 29, 196, 0.88)',
  plum84: 'rgba(135, 29, 196, 0.84)',
  plum80: 'rgba(135, 29, 196, 0.80)',
  plum76: 'rgba(135, 29, 196, 0.76)',
  plum72: 'rgba(135, 29, 196, 0.72)',
  plum68: 'rgba(135, 29, 196, 0.68)',
  plum64: 'rgba(135, 29, 196, 0.64)',
  plum60: 'rgba(135, 29, 196, 0.60)',
  plum56: 'rgba(135, 29, 196, 0.56)',
  plum52: 'rgba(135, 29, 196, 0.52)',
  plum48: 'rgba(135, 29, 196, 0.48)',
  plum44: 'rgba(135, 29, 196, 0.44)',
  plum40: 'rgba(135, 29, 196, 0.40)',
  plum36: 'rgba(135, 29, 196, 0.36)',
  plum32: 'rgba(135, 29, 196, 0.32)',
  plum28: 'rgba(135, 29, 196, 0.28)',
  plum24: 'rgba(135, 29, 196, 0.24)',
  plum20: 'rgba(135, 29, 196, 0.20)',
  plum16: 'rgba(135, 29, 196, 0.16)',
  plum12: 'rgba(135, 29, 196, 0.12)',
  plum8: 'rgba(135, 29, 196, 0.08)',
  plum4: 'rgba(135, 29, 196, 0.04)',
} as const;

Object.freeze(colors);
